import { effectiveDateRange, sortKeysBySerial } from '../../Helpers/keyRegisterUtils';
import { KeyType, RegisterKey } from '../../types/keyRegister';
import { matchesSearch, normalizeQuery } from './keyRegisterView';

/** One type in the "Lämna ut nycklar" picker with its in-stock keys. */
export interface HandOutGroup {
    type: KeyType;
    keys: RegisterKey[];
}

/**
 * The keys that can be handed out together: in stock, of a type that is not archived, grouped by
 * type A–Ö and sorted by serial. Ignores the page's filter. The search matches serial, type name,
 * förvaring and lock system; types without a match are left out.
 */
export const handOutGroups = (
    types: KeyType[],
    keysByType: { [typeKey: string]: RegisterKey[] },
    query: string,
): HandOutGroup[] => {
    const normalized = normalizeQuery(query);
    return [...types]
        .filter(type => !type.archived)
        .sort((a, b) => a.name.localeCompare(b.name, 'sv', { numeric: true }))
        .map(type => ({
            type,
            keys: sortKeysBySerial((keysByType[type.key] ?? [])
                .filter(key => key.status === 'inStock' && matchesSearch(key, type, undefined, normalized))),
        }))
        .filter(group => group.keys.length > 0);
};

/** Whether any key at all can be handed out (before searching). */
export const hasKeysToHandOut = (types: KeyType[], keysByType: { [typeKey: string]: RegisterKey[] }): boolean =>
    handOutGroups(types, keysByType, '').length > 0;

/**
 * The earliest Utlämnad den that fits every selected key: the latest of their `effectiveDateRange`
 * minimums, with the key that sets it (for the error text). No min when none of them has one.
 */
export const handOutDateLimit = (keys: RegisterKey[], today: string): { min?: string; blockingKey?: RegisterKey } => {
    let limit: { min?: string; blockingKey?: RegisterKey } = {};
    for (const key of keys) {
        const { min } = effectiveDateRange(key, today);
        if (min && (!limit.min || min > limit.min)) limit = { min, blockingKey: key };
    }
    return limit;
};
